// Lo solo SureKeep que se suelta al pasar a contado y se vuelve a apartar al regresar a SureKeep.
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { lineDiscounts } from './applySelection';
import {
    choicesOn,
    chosenPromotionIds,
    EMPTY_LINE,
    withSolo,
    withStackable,
    type PromotionChoice,
    type PromotionSelection,
} from './promotionSelection';

/** Una promo soltada, en la línea donde iba y con lo que bajaba. */
export interface ReleasedChoice {
    /** Iba en el teléfono: al volver se pone en el teléfono vigente. */
    onAnchor: boolean;
    lineId: string;
    solo: boolean;
    choice: PromotionChoice;
    amountCents: number;
}

/** Lo que se soltó al dejar SureKeep. */
export interface SureKeepReleased {
    /** Teléfono de la venta al soltar; con otro teléfono lo soltado ya no aplica. */
    anchorLineId?: string | null;
    choices: ReleasedChoice[];
    code: { code: string; amountCents: number; lineId: string } | null;
}

export const NOTHING_RELEASED: SureKeepReleased = { choices: [], code: null };

/** Lo soltado si sigue siendo del mismo teléfono (sin teléfono conocido, se conserva). */
export function releasedFor(
    released: SureKeepReleased | undefined,
    anchorLineId: string | null | undefined,
): SureKeepReleased {
    if (!released) return NOTHING_RELEASED;
    if (anchorLineId === undefined) return released;
    const sameAnchor =
        released.anchorLineId === undefined || released.anchorLineId === anchorLineId;
    return sameAnchor ? released : NOTHING_RELEASED;
}

export function isNothingReleased(released: SureKeepReleased | undefined): boolean {
    return !released || (released.choices.length === 0 && released.code === null);
}

const liveLinesOf = (session: WizardSession | undefined): ReservedLine[] =>
    (session?.reservedLines ?? []).filter((l) => l.status !== 'RELEASING');

/** Lo que baja el código en las líneas vivas y la línea donde va (el teléfono, si lo toca). */
export function heldCodeOf(
    session: WizardSession | undefined,
): { code: string; amountCents: number; lineId: string } | null {
    const first = liveLinesOf(session).find((l) => l.discountCode);
    const code = first?.discountCode;
    if (!first || !code) return null;
    const amountCents = liveLinesOf(session)
        .filter((l) => l.discountCode === code)
        .reduce((sum, l) => sum + (l.codeDiscountCents ?? 0), 0);
    return { code, amountCents, lineId: first.lineId };
}

/** Lo que baja una promo en una línea de la sesión. */
function amountOn(session: WizardSession | undefined, lineId: string, promotionId: string): number {
    const line = liveLinesOf(session).find((l) => l.lineId === lineId);
    if (!line) return 0;
    return lineDiscounts(line)
        .filter((d) => d.promotionId === promotionId)
        .reduce((sum, d) => sum + Math.max(0, d.discountCents), 0);
}

/** Lo solo SureKeep que hay en la selección: sus promos y el código si es solo SureKeep. */
export function sureKeepOnlyHeld(
    selection: PromotionSelection,
    isSureKeepOnly: (promotionId: string) => boolean,
    codeIsSureKeepOnly: (code: string) => boolean,
): { promotionIds: string[]; code: string | null } {
    return {
        promotionIds: chosenPromotionIds(selection).filter(isSureKeepOnly),
        code: selection.code && codeIsSureKeepOnly(selection.code) ? selection.code : null,
    };
}

/** Lo que había en `before` y ya no quedó en `after`, solo de lo que era solo SureKeep. */
export function releasedBetween(
    before: PromotionSelection,
    after: PromotionSelection,
    held: { promotionIds: readonly string[]; code: string | null },
    session: WizardSession | undefined,
    evaluatedCodeCents?: number,
): SureKeepReleased {
    const kept = new Set(chosenPromotionIds(after));
    const ids = new Set(held.promotionIds.filter((id) => !kept.has(id)));
    const choices: ReleasedChoice[] = [];
    for (const lineId of Object.keys(before.byLine)) {
        const line = before.byLine[lineId] ?? EMPTY_LINE;
        for (const choice of choicesOn(before, lineId)) {
            if (!ids.has(choice.promotionId)) continue;
            choices.push({
                onAnchor: lineId === before.anchorLineId,
                lineId,
                solo: line.solo?.promotionId === choice.promotionId,
                choice,
                amountCents: amountOn(session, lineId, choice.promotionId),
            });
        }
    }
    const heldCode = heldCodeOf(session);
    const code =
        held.code && after.code !== held.code
            ? {
                  code: held.code,
                  amountCents:
                      heldCode?.code === held.code && heldCode.amountCents > 0
                          ? heldCode.amountCents
                          : (evaluatedCodeCents ?? 0),
                  lineId: heldCode?.lineId ?? before.anchorLineId ?? '',
              }
            : null;
    return { anchorLineId: before.anchorLineId, choices, code };
}

/** Suma lo soltado a lo memorizado (una pasada nueva por contado no borra la anterior). */
export function mergeReleased(a: SureKeepReleased, b: SureKeepReleased): SureKeepReleased {
    if (a.anchorLineId !== b.anchorLineId) return b;
    const seen = new Set(b.choices.map((c) => `${c.lineId}|${c.choice.promotionId}`));
    return {
        anchorLineId: b.anchorLineId,
        choices: [
            ...a.choices.filter((c) => !seen.has(`${c.lineId}|${c.choice.promotionId}`)),
            ...b.choices,
        ],
        code: b.code ?? a.code,
    };
}

/** La selección con lo soltado de vuelta; lo de líneas que ya no están se queda fuera. */
export function withReleased(
    selection: PromotionSelection,
    released: SureKeepReleased,
    liveLineIds: readonly string[],
): PromotionSelection {
    const live = new Set(liveLineIds);
    let next = selection;
    const known = new Set(chosenPromotionIds(selection));
    for (const r of released.choices) {
        const lineId = r.onAnchor ? next.anchorLineId : r.lineId;
        if (!lineId || !live.has(lineId) || known.has(r.choice.promotionId)) continue;
        next = r.solo
            ? next.byLine[lineId]?.solo
                ? next
                : withSolo(next, lineId, r.choice)
            : withStackable(next, lineId, r.choice, true);
    }
    return released.code && !next.code ? { ...next, code: released.code.code } : next;
}
